import { useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import * as api from "../api/client";

/**
 * Share a dashboard with roles (admin, owner only). Users holding a picked role see the same dashboard,
 * read-only, in their sidebar.
 *
 * A role that is not granted every table and column this dashboard reads is listed but cannot be
 * picked — sharing would show it numbers it may not query — and the reason is shown under it. The
 * server enforces the same rule (see app/dashboards.share_dashboard); this only explains it up front.
 */
export default function ShareDashboardModal({ dashboardId, onSaved, onClose }) {
  const [roles, setRoles] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getDashboardSharing(dashboardId)
      .then((r) => {
        if (cancelled) return;
        setRoles(r.roles || []);
        setPicked(new Set(r.sharedRoles || []));
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [dashboardId]);

  const eligible = (roles || []).filter((r) => r.eligible);
  const allPicked = eligible.length > 0 && eligible.every((r) => picked.has(r.name));

  function toggle(name) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleAll() {
    setPicked(allPicked ? new Set() : new Set(eligible.map((r) => r.name)));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      // Only eligible roles are ever sent: one that lost eligibility since it was shared is dropped.
      const res = await api.setDashboardSharing(dashboardId, [...picked].filter((n) => eligible.some((r) => r.name === n)));
      onSaved(res.sharedRoles || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-modal" style={{ width: "min(560px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-head">
          <div className="form-modal-title">Share dashboard with roles</div>
          <button className="icon-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>
        <Form onSubmit={submit}>
          <div className="form-modal-body">
            <div className="col-desc-hint">
              Users in a selected role see this dashboard, read-only, marked <span className="dash-shared-badge">G</span> in
              their sidebar. Roles without access to every table and column it uses can't be selected.
            </div>
            {error && <Alert variant="danger">{error}</Alert>}
            {roles === null ? (
              !error && <Spinner animation="border" size="sm" />
            ) : roles.length === 0 ? (
              <div className="col-desc-hint">No roles exist yet. Create one on the Roles page.</div>
            ) : (
              <>
                <Form.Check
                  type="checkbox"
                  id="share-all-roles"
                  className="share-all"
                  label={`All eligible roles (${eligible.length})`}
                  checked={allPicked}
                  disabled={eligible.length === 0}
                  onChange={toggleAll}
                />
                <div className="role-table-list">
                  {roles.map((r) => (
                    <div key={r.name} className={"role-table-row" + (r.eligible ? "" : " is-ineligible")}>
                      <div className="role-table-row-head">
                        <Form.Check
                          type="checkbox"
                          id={`share-role-${r.name}`}
                          label={r.name}
                          checked={r.eligible && picked.has(r.name)}
                          disabled={!r.eligible}
                          onChange={() => toggle(r.name)}
                        />
                        {!r.eligible && <span className="pill warning">no access</span>}
                        {r.eligible && r.shared && <span className="pill neutral">shared</span>}
                      </div>
                      {!r.eligible && (
                        <div className="share-missing">
                          Missing {r.missing.slice(0, 3).join(", ")}
                          {r.missing.length > 3 ? ` and ${r.missing.length - 3} more` : ""}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="form-modal-foot">
            <Button variant="outline-secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving || roles === null}>
              {saving ? <Spinner animation="border" size="sm" /> : picked.size ? "Save sharing" : "Make private"}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
